import React, {
  ChangeEvent,
  ComponentProps,
  useEffect,
  useMemo,
  useState,
} from 'react'

import styled from '@emotion/styled'
import { useDebouncedCallback } from 'use-debounce'

import { isFunction } from '@surglogs/util.is-function'

import { useHandler } from '@surglogs-app/hooks'
import { Search } from '@surglogs-app/icons'

import { Input } from './Input'
import { TextButton } from './TextButton'

export const SEARCH_INPUT_DEBOUNCE_TIME = 300

export const SearchInputCancelButton = styled(TextButton)`
  &:hover {
    color: ${({ theme, disabled }) =>
      disabled ? theme.ui.colors['navy.25'] : theme.ui.colors['blue.100']};
  }
`

interface SearchInputProps
  extends Omit<ComponentProps<typeof Input>, 'onChange'> {
  cancelMessage?: string
  onCancel?: (() => void) | null
  onChange: (search: string) => void
  value?: string
}

export const SearchInput = ({
  cancelMessage = 'Cancel',
  disabled,
  onCancel,
  onChange,
  value,
  ...rest
}: SearchInputProps) => {
  const [inputValue, setInputValue] = useState(value)
  const allowCancel = isFunction(onCancel) && value
  const debounced = useDebouncedCallback(onChange, SEARCH_INPUT_DEBOUNCE_TIME)

  const onChangeHandler = useHandler((e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
    debounced(e.target.value)
  })

  useEffect(() => {
    setInputValue(value)
  }, [value])

  const adornment = useMemo(() => {
    if (allowCancel) {
      return (
        <SearchInputCancelButton
          {...{
            onClick: onCancel,
            variant: 'default',
            disabled,
          }}>
          {cancelMessage}
        </SearchInputCancelButton>
      )
    }

    return null
  }, [allowCancel, cancelMessage, disabled, onCancel])

  return (
    <Input
      {...rest}
      {...{
        icon: Search,
        value: inputValue,
        disabled,
        onChange: onChangeHandler,
        'aria-label': 'searchbox',
        wrapperProps: { flex: 1 },
        adornment,
      }}
    />
  )
}
